const { getInsertProperties } = require('../../tools/sql-tools')

class ElectronicInvoicingPgAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  /**
   * @param {string[]} customerIds
   * @param {'NG' | 'KE'} companyCode
  */
  async getQboCustomerDetail (customerIds, companyCode) {
    const {rows} = await this.pgConnection.query(`
      select
        ql.quickbooks_customer_id as "customerId",
        ql."company_code" as "companyCode",
        l.name_of_pharmacy as "customerName",
        l.email,
        l.lga,
        l.state,
        l.owners_phone_number as "phoneNumber",
        l.classification,
        l.membership_type as "membershipType",
        l.state,
        l.city,
        l.address,
        m.id as "marketId",
        m.emails as "marketEmails",
        null as "taxIdNumber",
        l.tax_id_number_encrypted as "taxIdNumberEncrypted"
      from avocado.data_quickbookslocation ql
      inner join avocado.data_location l on ql.location_id = l."uuid"
      left join avocado.data_market m on m.id = l.market_id 
      where ql.quickbooks_customer_id = any($1) and ql."company_code" = $2`,
    [customerIds, companyCode]
    )
    return rows
  }

  async getProductCategories (skuNames) {
    if (Array.isArray(skuNames) && skuNames.length > 0) {
      const {rows} = await this.pgConnection.query(`
        select 
          pb.category,
          ps.display_name as "productName"
        from avocado.data_productsku ps
        left join avocado.data_productpresentation pp on pp.id = ps.presentation_id 
        left join avocado.data_productbase pb on pb.id = pp.product_id
        where display_name = any($1)
        group by 1, 2`,
      [skuNames]
      )
      return rows
    }
    return []
  }

  async upsert (upsertRows, companyCode) {
    const { columns, values, placeholders } = getInsertProperties(upsertRows)
    const nextOffset = values.length + 1

    const { rows } = await this.pgConnection.query(`
      with insert_raw as (
        insert into fin.raw_electronic_invoice (${columns})
        values ${placeholders}
        ON CONFLICT (invoice_number) DO UPDATE SET
          data = EXCLUDED.data,
          response_code = EXCLUDED.response_code,
          response_message = EXCLUDED.response_message,
          provider = EXCLUDED.provider,
          updated_at = now()
        returning *
      ),
      transformed_raw as (
        select
          -- For QBO CreditMemos and Invoices share the same txn_id number interator so its safe to do coalesce check on both 
          -- As either of the two would be uniquely present
          coalesce(i.origin, c.origin) as origin,
          coalesce(i.txn_type, c.txn_type) as txn_type,
          coalesce(i.company, c.company) as company,
          coalesce(i.txn_id, c.txn_id) as txn_id,
          (ir.data ->> 'invoiceBusinessId') as business_id,
          (ir.data ->> 'invoiceNumber') as invoice_number,
          (ir.data ->> 'invoiceIrn') as invoice_irn,
          (ir.data ->> 'invoicePaymentStatus') as payment_status,
          (ir.data ->> 'invoiceNote') as invoice_note,
          (ir.data ->> 'invoiceTypeCode') as type_code,
          (ir.data ->> 'invoiceDocumentCurrencyCode') as document_currency_code,
          (ir.data ->> 'invoiceTaxCurrencyCode') as tax_currency_code,
          (ir.data ->> 'invoiceLineExtensionAmount')::numeric as line_extension_amount,
          (ir.data ->> 'invoicePayableAmount')::numeric as payable_amount,
          (ir.data ->> 'invoiceTaxInclusiveAmount')::numeric as tax_inclusive_amount,
          (ir.data ->> 'invoiceTaxExclusiveAmount')::numeric as tax_exclusive_amount,
          (ir.data ->> 'invoiceIssueDate')::date as issue_date,
          ir.id as raw_id
        from insert_raw ir
        -- We store the actual invoice doc number in the raw invoiceNote property
        left join fin.invoice i on i.doc_no = (ir.data ->> 'invoiceNote')::text and i.company = $${nextOffset}
        left join fin.creditmemo c on c.doc_no = (ir.data ->> 'invoiceNote')::text and c.company = $${nextOffset}
      ),
      insert_transformed as (
        insert into fin.electronic_invoice (
          origin,
          txn_type,
          company,
          txn_id,
          business_id,
          invoice_number,
          invoice_irn,
          payment_status,
          invoice_note,
          type_code,
          document_currency_code,
          tax_currency_code,
          line_extension_amount,
          payable_amount,
          tax_inclusive_amount,
          tax_exclusive_amount,
          issue_date,
          raw_id
        )
        select
          origin,
          txn_type,
          company,
          txn_id,
          business_id,
          invoice_number,
          invoice_irn,
          payment_status,
          invoice_note,
          type_code,
          document_currency_code,
          tax_currency_code,
          line_extension_amount,
          payable_amount,
          tax_inclusive_amount,
          tax_exclusive_amount,
          issue_date,
          raw_id
        from transformed_raw
        ON CONFLICT (invoice_number) DO UPDATE SET
          origin = EXCLUDED.origin,
          txn_type = EXCLUDED.txn_type,
          company = EXCLUDED.company,
          txn_id = EXCLUDED.txn_id,
          business_id = EXCLUDED.business_id,
          invoice_number = EXCLUDED.invoice_number,
          invoice_irn = EXCLUDED.invoice_irn,
          payment_status = EXCLUDED.payment_status,
          invoice_note = EXCLUDED.invoice_note,
          type_code = EXCLUDED.type_code,
          document_currency_code = EXCLUDED.document_currency_code,
          tax_currency_code = EXCLUDED.tax_currency_code,
          line_extension_amount = EXCLUDED.line_extension_amount,
          payable_amount = EXCLUDED.payable_amount,
          tax_inclusive_amount = EXCLUDED.tax_inclusive_amount,
          tax_exclusive_amount = EXCLUDED.tax_exclusive_amount,
          issue_date = EXCLUDED.issue_date,
          updated_at = now()
        returning
          id,
          raw_id as "rawId",
          origin,
          txn_type as "txnType",
          company,
          txn_id as "txnId",
          invoice_irn as "invoiceIrn",
          invoice_number as "invoiceNumber",
          payment_status as "paymentStatus",
          type_code as "typeCode",
          invoice_note as "docNumber",
          tax_inclusive_amount as "taxInclusiveAmount",
          tax_exclusive_amount as "taxExclusiveAmount",
          payable_amount as "payableAmount",
          line_extension_amount as "lineExtensionAmount"
      )
      select * from insert_transformed
    `, [...values, companyCode])
    return rows
  }

  /**
   * @param {string[]} invoiceTxnIds
   * @param {'NG' | 'KE'} companyCode
  */
  async getInvoiceLoanAccount (invoiceTxnIds, companyCode) {
    const {rows} = await this.pgConnection.query(`
      with journal_loan_accounts as (
        select 
          a.origin,
          a.company,
          a.account_id,
          a.balance,
          a.active,
          jl.customer_id,
          jl.txn_id as journal_txn_id,
          jl.amount as journal_amount,
          jl.description 
        from fin.account a
        inner join fin.journalentry_line jl on a.account_id = jl.account_id and a.company = jl.company 
        where a.account_subtype = 'LoansToOthers' and a.company = $2  and jl."entry_type" = 'debit'
      ),
      journal_linked_payments as (
        select
          pl.txn_id as payment_txn_id,
          pl.company,
          pl.origin,
          ja.journal_txn_id,
          ja.journal_amount
        from journal_loan_accounts ja
        left join fin.payment_line pl on ja.journal_txn_id = pl.linked_id and ja.company = pl.company and ja.origin = pl.origin 
        where pl.linked_type = 'journalentry'
        group by 1, 2, 3, 4, 5
      ),
      journal_linked_invoices as (
        select
          jp.payment_txn_id,
          jp.journal_txn_id,
          pl.company,
          pl.origin,
          jp.journal_amount,
          pl.linked_id as invoice_txn_id,
          coalesce (
            (
              select jsonb_agg(il.txn_id)
              from (
                select pl2.linked_id as txn_id from fin.payment_line pl2
                where pl2.txn_id = jp.payment_txn_id and pl2.company = pl.company and pl2.origin = pl.origin and pl2.linked_type = 'invoice'
              ) il
            ),
            '[]'::jsonb
          ) as invoice_txn_ids
        from journal_linked_payments jp
        left join fin.payment_line pl on jp.payment_txn_id = pl.txn_id and jp.company = pl.company and jp.origin = pl.origin
        where pl.linked_type = 'invoice' and pl.linked_id = any($1)
        group by 1, 2, 3, 4, 5, 6
      )
      select
        ja.origin,
        ja.company,
        ja.account_id as "loanAccountId",
        ja.balance::float as balance,
        ja.active,
        ja.customer_id as "customerId",
        ja.journal_txn_id as "journalTxnId",
        ja.journal_amount::float as "journalAmount",
        ja.description,
        ji.invoice_txn_id as "invoiceTxnId",
        ji.invoice_txn_ids as "linkedInvoicesTxnIds"
      from journal_loan_accounts ja
      inner join journal_linked_invoices ji on ja.journal_txn_id = ji.journal_txn_id and ja.company = ji.company and ja.origin = ji.origin
    `, [invoiceTxnIds, companyCode])
    return rows
  }
}

module.exports = ElectronicInvoicingPgAdapter
